// src/components/EmployeeManagementPage.js
import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc
} from 'firebase/firestore';

import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Select,
  MenuItem,
  Button,
  TextField
} from '@mui/material';

import { useTenant } from '../../contexts/TenantContext';

function EmployeeManagementPage() {
  // States
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [employeeLimit, setEmployeeLimit] = useState(null);

  // Für neues Mitarbeiterformular
  const [newEmployee, setNewEmployee] = useState({ name: '', email: '', role: 'employee' });

  // Für Editiermodus pro Mitarbeiter
  const [editEmployeeId, setEditEmployeeId] = useState(null);
  const [editEmployeeData, setEditEmployeeData] = useState({ name: '', email: '', role: '' });

  // Tenant-ID via Context
  const { tenantId } = useTenant();

  // 1) Beim Laden: Mitarbeiter und Limit abrufen
  useEffect(() => {
    if (!tenantId) return; // warten, bis tenantId verfügbar
    const fetchEmployeesAndLimit = async () => {
      setLoading(true);
      try {
        const tenantDocRef = doc(db, 'tenants', tenantId);
        const tenantSnap = await getDoc(tenantDocRef);
        if (tenantSnap.exists()) {
          const tenantData = tenantSnap.data();
          setEmployeeLimit(tenantData.employeeLimit || null); // null bedeutet kein Limit gesetzt
        } else {
          setError('Tenant-Daten nicht gefunden.');
        }

        const employeesRef = collection(db, 'tenants', tenantId, 'employees');
        const snapshot = await getDocs(employeesRef);
        const list = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setEmployees(list);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Fehler beim Abrufen der Mitarbeiterliste oder des Limits.');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeesAndLimit();
  }, [tenantId]);

  // 2) Neuen Mitarbeiter hinzufügen
  const handleCreateEmployee = async () => {
    // Form-Validierung
    if (!newEmployee.name || !newEmployee.email) {
      setError('Bitte Name und E-Mail angeben.');
      return;
    }

    // Überprüfen, ob das Limit erreicht ist (nur wenn ein Limit gesetzt ist)
    if (employeeLimit !== null && employees.length >= employeeLimit) {
      setError('Mitarbeiterlimit erreicht. Bitte kontaktieren Sie den Administrator.');
      return;
    }

    try {
      const employeesRef = collection(db, 'tenants', tenantId, 'employees');
      const docRef = await addDoc(employeesRef, {
        name: newEmployee.name,
        email: newEmployee.email,
        role: newEmployee.role,
      });

      // State aktualisieren
      setEmployees([
        ...employees,
        { id: docRef.id, ...newEmployee },
      ]);
      setMessage('Mitarbeiter erfolgreich angelegt.');
      setNewEmployee({ name: '', email: '', role: 'employee' });
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Fehler beim Anlegen des Mitarbeiters.');
    }
  };

  // 3) Mitarbeiter bearbeiten (Formularwerte füllen)
  const handleEditClick = (emp) => {
    setEditEmployeeId(emp.id);
    setEditEmployeeData({
      name: emp.name || '',
      email: emp.email || '',
      role: emp.role || 'employee',
    });
    setMessage(null);
    setError(null);
  };

  // 4) Änderungen speichern
  const handleSaveEdit = async () => {
    if (!editEmployeeData.name || !editEmployeeData.email) {
      setError('Bitte Name und E-Mail ausfüllen.');
      return;
    }

    try {
      const empDocRef = doc(db, 'tenants', tenantId, 'employees', editEmployeeId);
      await updateDoc(empDocRef, {
        name: editEmployeeData.name,
        email: editEmployeeData.email,
        role: editEmployeeData.role,
      });

      // Lokalen State aktualisieren
      setEmployees(
        employees.map((emp) =>
          emp.id === editEmployeeId
            ? { ...emp, ...editEmployeeData }
            : emp
        )
      );
      setMessage('Mitarbeiterdaten erfolgreich aktualisiert.');
      setEditEmployeeId(null);
      setEditEmployeeData({ name: '', email: '', role: '' });
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Fehler beim Speichern der Änderungen.');
    }
  };

  // 5) Bearbeiten abbrechen
  const handleCancelEdit = () => {
    setEditEmployeeId(null);
    setEditEmployeeData({ name: '', email: '', role: '' });
  };

  // 6) Mitarbeiter löschen (optional)
  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Möchten Sie diesen Mitarbeiter wirklich löschen?')) {
      return;
    }
    try {
      const empDocRef = doc(db, 'tenants', tenantId, 'employees', id);
      await deleteDoc(empDocRef);
      // State aktualisieren
      setEmployees(employees.filter((emp) => emp.id !== id));
      setMessage('Mitarbeiter erfolgreich gelöscht.');
    } catch (err) {
      console.error(err);
      setError('Fehler beim Löschen des Mitarbeiters.');
    }
  };

  // Loading / Error-Anzeige
  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />;
  }
  if (error && !employees.length) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Überprüfung, ob die Mitarbeiterzahl das Limit überschreitet
  const isOverLimit = employeeLimit !== null && employees.length > employeeLimit;

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Mitarbeiterverwaltung
      </Typography>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* Hinweis bei erreichtem oder überschrittenem Limit */}
      {employeeLimit !== null && (
        <>
          {employees.length > employeeLimit ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Die aktuelle Mitarbeiterzahl ({employees.length}) überschreitet das festgelegte Limit von {employeeLimit}. Bitte entfernen Sie einige Mitarbeiter oder erhöhen Sie das Limit.
            </Alert>
          ) : employees.length === employeeLimit ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Das maximale Mitarbeiterlimit von {employeeLimit} wurde erreicht. Sie können keine weiteren Mitarbeiter hinzufügen.
            </Alert>
          ) : null}
        </>
      )}

      {/* Formular zum Anlegen neuer Mitarbeiter */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          mb: 4,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          label="Name"
          variant="outlined"
          value={newEmployee.name}
          onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
        />
        <TextField
          label="E-Mail"
          type="email"
          variant="outlined"
          value={newEmployee.email}
          onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
        />
        <Select
          label="Rolle"
          value={newEmployee.role}
          onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })}
          displayEmpty
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="employee">Mitarbeiter</MenuItem>
          {/* Optional weitere Rollen */}
        </Select>
        <Button 
          variant="contained" 
          onClick={handleCreateEmployee}
          disabled={
            employeeLimit !== null 
            && (employees.length >= employeeLimit || isOverLimit)
          }
        >
          Mitarbeiter hinzufügen
        </Button>
      </Box>

      {/* Tabelle mit allen Mitarbeitern */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((emp) => {
              const isEditing = editEmployeeId === emp.id;
              return (
                <TableRow key={emp.id}>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        variant="outlined"
                        value={editEmployeeData.name}
                        onChange={(e) =>
                          setEditEmployeeData({ ...editEmployeeData, name: e.target.value })
                        }
                      />
                    ) : (
                      emp.name
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        type="email"
                        variant="outlined"
                        value={editEmployeeData.email}
                        onChange={(e) =>
                          setEditEmployeeData({ ...editEmployeeData, email: e.target.value })
                        }
                      />
                    ) : (
                      emp.email
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Select
                        value={editEmployeeData.role}
                        onChange={(e) =>
                          setEditEmployeeData({ ...editEmployeeData, role: e.target.value })
                        }
                        displayEmpty
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="employee">Mitarbeiter</MenuItem>
                        {/* Optional weitere Rollen */}
                      </Select>
                    ) : (
                      emp.role
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{ mr: 1 }}
                          onClick={handleSaveEdit}
                        >
                          Speichern
                        </Button>
                        <Button variant="outlined" onClick={handleCancelEdit}>
                          Abbrechen
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{ mr: 1 }}
                          onClick={() => handleEditClick(emp)}
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleDeleteEmployee(emp.id)}
                        >
                          Löschen
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EmployeeManagementPage;
